import { useEffect, useMemo, useState } from 'react'
import { BarChart3, Download, FileText, RefreshCw } from 'lucide-react'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import autoTable from 'jspdf-autotable'
import { createThaiPDF, thaiTableStyles, thaiHeadStyles } from '@/src/lib/pdf/thai-font'
import { generateBankUsageCreditsReport } from '@/src/lib/api/admin-reports'
import type {
  ReportDateRange,
  BankUsageCreditsReportData,
} from '@/src/types/admin/reports'

function toLocalISODate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function defaultRange(): ReportDateRange {
  const now = new Date()
  // First day of the current month
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  // Last day of the current month (day 0 of next month)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    date_from: toLocalISODate(from),
    date_to: toLocalISODate(to),
  }
}

const fmtN = (v: number) => v.toLocaleString('th-TH')

const formatDateLabel = (date: string) => {
  const d = new Date(date)
  if (Number.isNaN(d.getTime())) return date
  return d.toLocaleDateString('th-TH', { day: '2-digit', month: 'short' })
}

export default function AdminReportBankUsageCredits() {
  const [range, setRange] = useState<ReportDateRange>(defaultRange)
  const [loading, setLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [data, setData] = useState<BankUsageCreditsReportData | null>(null)
  const [selectedBankCodes, setSelectedBankCodes] = useState<string[]>([])
  const [dailyChartMode, setDailyChartMode] = useState<'total' | 'stacked'>('total')

  const availableBankCodes = useMemo(
    () => (data?.banks ?? []).map((bank) => bank.bank_code),
    [data]
  )

  const loadData = async () => {
    setLoading(true)
    try {
      const payload: ReportDateRange = {
        date_from: range.date_from,
        date_to: range.date_to,
      }
      if (selectedBankCodes.length > 0) {
        payload.bank_codes = selectedBankCodes
      }

      const result = await generateBankUsageCreditsReport(payload)
      setData(result.data)

      const incomingBanks = (result.data?.banks ?? []).map((bank) => bank.bank_code)
      setSelectedBankCodes((prev) => {
        if (incomingBanks.length === 0) return []
        if (prev.length === 0) return incomingBanks

        const next = prev.filter((code) => incomingBanks.includes(code))
        return next.length > 0 ? next : incomingBanks
      })

      if (!result.data?.banks?.length) {
        toast.info('ไม่มีข้อมูลในช่วงวันที่ที่เลือก')
      }
    } catch {
      toast.error('ไม่สามารถโหลดรายงานการใช้งานเครดิตรายธนาคารได้')
    } finally {
      setLoading(false)
    }
  }

  const byBankChartData = useMemo(() => {
    if (!data?.banks) return []
    const bankSet = new Set(selectedBankCodes)
    return data.banks
      .filter((b) => selectedBankCodes.length === 0 || bankSet.has(b.bank_code))
      .map((b) => ({
      bank_code: b.bank_code,
      credits_used: b.total_credits_used,
      total_requests: b.total_requests,
      success_rate_pct: b.success_rate_pct,
    }))
  }, [data, selectedBankCodes])

  const dailyStackedChartData = useMemo(() => {
    if (!data?.daily_by_bank) return []

    const bankSet = new Set(selectedBankCodes)
    const filtered = data.daily_by_bank.filter(
      (row) => selectedBankCodes.length === 0 || bankSet.has(row.bank_code)
    )

    const dateMap = new Map<string, Record<string, number | string>>()
    filtered.forEach((row) => {
      if (!dateMap.has(row.date)) {
        dateMap.set(row.date, {
          date: row.date,
          date_label: formatDateLabel(row.date),
          total_credits_used: 0,
        })
      }
      const point = dateMap.get(row.date)
      if (!point) return
      point[row.bank_code] = Number(point[row.bank_code] || 0) + row.credits_used
      point.total_credits_used = Number(point.total_credits_used || 0) + row.credits_used
    })

    return Array.from(dateMap.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)))
  }, [data, selectedBankCodes])

  const dailyChartData = useMemo(
    () => dailyStackedChartData.map((row) => ({
      date: String(row.date),
      date_label: String(row.date_label),
      total_credits_used: Number(row.total_credits_used || 0),
    })),
    [dailyStackedChartData]
  )

  // Derive the distinct bank codes that actually appear in the filtered stacked data
  // (used for <Bar> rendering — avoids empty chart when selectedBankCodes is [])
  const stackedBankCodes = useMemo(() => {
    const seen = new Set<string>()
    dailyStackedChartData.forEach((point) => {
      Object.keys(point).forEach((k) => {
        if (k !== 'date' && k !== 'date_label' && k !== 'total_credits_used') seen.add(k)
      })
    })
    return Array.from(seen).sort()
  }, [dailyStackedChartData])

  const filteredBanks = useMemo(
    () => (data?.banks ?? []).filter((b) => selectedBankCodes.length === 0 || selectedBankCodes.includes(b.bank_code)),
    [data, selectedBankCodes]
  )

  const summaryForView = useMemo(() => {
    const overview = {
      total_requests: 0,
      successful_requests: 0,
      failed_requests: 0,
      total_credits_used: 0,
    }

    filteredBanks.forEach((bank) => {
      overview.total_requests += bank.total_requests
      overview.successful_requests += bank.successful_requests
      overview.failed_requests += bank.failed_requests
      overview.total_credits_used += bank.total_credits_used
    })

    const topBank = filteredBanks[0]
    return {
      ...overview,
      success_rate_pct:
        overview.total_requests > 0
          ? Number(((overview.successful_requests / overview.total_requests) * 100).toFixed(1))
          : 0,
      top_bank_code: topBank?.bank_code ?? null,
      top_bank_credits_used: topBank?.total_credits_used ?? 0,
    }
  }, [filteredBanks])

  const toggleBankCode = (bankCode: string) => {
    setSelectedBankCodes((prev) =>
      prev.includes(bankCode) ? prev.filter((code) => code !== bankCode) : [...prev, bankCode]
    )
  }

  const selectAllBanks = () => {
    setSelectedBankCodes(availableBankCodes)
  }

  const clearAllBanks = () => {
    setSelectedBankCodes([])
  }

  const handleExcelExport = () => {
    if (!data || filteredBanks.length === 0) {
      toast.info('ไม่มีข้อมูลสำหรับ export')
      return
    }

    const bankSummaryRows = filteredBanks.map((row) => ({
      bank_code: row.bank_code,
      total_credits_used: row.total_credits_used,
      total_requests: row.total_requests,
      successful_requests: row.successful_requests,
      failed_requests: row.failed_requests,
      success_rate_pct: row.success_rate_pct,
      avg_credits_per_request: row.avg_credits_per_request,
    }))

    const dailyRows = dailyChartData.map((row) => ({
      date: row.date,
      total_credits_used: row.total_credits_used,
    }))

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bankSummaryRows), 'Bank Summary')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dailyRows), 'Daily Summary')
    XLSX.writeFile(wb, `bank_usage_credits_${range.date_from}_${range.date_to}.xlsx`)
    toast.success('ดาวน์โหลด Excel สำเร็จ')
  }

  const handlePdfExport = async () => {
    if (!data || filteredBanks.length === 0) {
      toast.info('ไม่มีข้อมูลสำหรับ export')
      return
    }

    setPdfLoading(true)
    try {
      const doc = await createThaiPDF()
      doc.setFontSize(14)
      doc.text('รายงานการใช้งานเครดิตรายธนาคาร', 14, 16)
      doc.setFontSize(8)
      doc.text(`${range.date_from} ถึง ${range.date_to}`, 14, 22)

      autoTable(doc, {
        startY: 28,
        columns: [
          { header: 'ธนาคาร', dataKey: 'bank_code' },
          { header: 'เครดิตที่ใช้', dataKey: 'total_credits_used' },
          { header: 'คำขอทั้งหมด', dataKey: 'total_requests' },
          { header: 'สำเร็จ', dataKey: 'successful_requests' },
          { header: 'ล้มเหลว', dataKey: 'failed_requests' },
          { header: 'Success Rate(%)', dataKey: 'success_rate_pct' },
          { header: 'เครดิต/คำขอ', dataKey: 'avg_credits_per_request' },
        ],
        body: filteredBanks.map((row) => ({
          bank_code: row.bank_code,
          total_credits_used: fmtN(row.total_credits_used),
          total_requests: fmtN(row.total_requests),
          successful_requests: fmtN(row.successful_requests),
          failed_requests: fmtN(row.failed_requests),
          success_rate_pct: row.success_rate_pct.toFixed(1),
          avg_credits_per_request: row.avg_credits_per_request.toFixed(2),
        })),
        styles: { ...thaiTableStyles, fontSize: 7 },
        headStyles: { ...thaiHeadStyles, fillColor: [20, 184, 166] },
      })

      doc.save(`bank_usage_credits_${range.date_from}_${range.date_to}.pdf`)
      toast.success('ดาวน์โหลด PDF สำเร็จ')
    } catch {
      toast.error('ไม่สามารถสร้าง PDF ได้')
    } finally {
      setPdfLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // Run once on page mount for auto-initial load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg shrink-0">
            <BarChart3 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">รายงานการใช้งานเครดิตรายธนาคาร</h1>
            <p className="text-sm text-gray-500">ภาพรวมทั้งระบบแบบรายวัน แยกตามธนาคาร</p>
          </div>
        </div>
        <Badge variant="secondary" className="shrink-0">Admin</Badge>
      </div>

      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs mb-1 block">วันที่เริ่ม</Label>
              <Input
                type="date"
                value={range.date_from}
                onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))}
                className="h-9 text-sm w-40"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">วันที่สิ้นสุด</Label>
              <Input
                type="date"
                value={range.date_to}
                onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))}
                className="h-9 text-sm w-40"
              />
            </div>
            <Button onClick={loadData} disabled={loading} className="h-9">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              โหลดข้อมูล
            </Button>
            <Button
              variant="outline"
              onClick={handleExcelExport}
              disabled={!data || filteredBanks.length === 0}
              className="h-9"
            >
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button
              variant="outline"
              onClick={handlePdfExport}
              disabled={!data || filteredBanks.length === 0 || pdfLoading}
              className="h-9"
            >
              <FileText className="h-4 w-4 mr-2" />
              {pdfLoading ? 'กำลังสร้าง...' : 'PDF'}
            </Button>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">ธนาคาร (เลือกได้หลายค่า)</Label>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={selectAllBanks} className="h-7 px-2 text-xs">
                  เลือกทั้งหมด
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={clearAllBanks} className="h-7 px-2 text-xs">
                  ล้าง
                </Button>
              </div>
            </div>

            {availableBankCodes.length === 0 ? (
              <p className="text-xs text-gray-400">โหลดข้อมูลก่อนเพื่อแสดงรายการธนาคาร</p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {availableBankCodes.map((bankCode) => (
                  <label key={bankCode} className="inline-flex items-center gap-2 rounded border px-2 py-1 text-xs">
                    <Checkbox
                      checked={selectedBankCodes.includes(bankCode)}
                      onCheckedChange={() => toggleBankCode(bankCode)}
                    />
                    <span>{bankCode}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {data && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-1 pt-4 px-4">
              <CardDescription className="text-xs">เครดิตที่ใช้ทั้งหมด</CardDescription>
              <CardTitle className="text-lg">{fmtN(summaryForView.total_credits_used)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-1 pt-4 px-4">
              <CardDescription className="text-xs">จำนวนคำขอทั้งหมด</CardDescription>
              <CardTitle className="text-lg">{fmtN(summaryForView.total_requests)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-1 pt-4 px-4">
              <CardDescription className="text-xs">Success Rate</CardDescription>
              <CardTitle className="text-lg">{summaryForView.success_rate_pct.toFixed(1)}%</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-1 pt-4 px-4">
              <CardDescription className="text-xs">ธนาคารที่ใช้เครดิตสูงสุด</CardDescription>
              <CardTitle className="text-lg">{summaryForView.top_bank_code || '-'}</CardTitle>
              <CardDescription className="text-xs">
                {fmtN(summaryForView.top_bank_credits_used)} เครดิต
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-medium">เครดิตที่ใช้รายวัน</CardTitle>
                <CardDescription>สลับมุมมองรวม หรือแบบ stacked แยกธนาคาร</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={dailyChartMode === 'total' ? 'default' : 'outline'}
                  onClick={() => setDailyChartMode('total')}
                >
                  รวม
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={dailyChartMode === 'stacked' ? 'default' : 'outline'}
                  onClick={() => setDailyChartMode('stacked')}
                >
                  Stacked
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {dailyChartData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">ไม่มีข้อมูล</div>
            ) : (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  {dailyChartMode === 'total' ? (
                    <AreaChart data={dailyChartData} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date_label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [fmtN(Number(value || 0)), 'เครดิต']} />
                      <Area type="monotone" dataKey="total_credits_used" stroke="#2563eb" fill="#93c5fd" name="เครดิตที่ใช้" />
                    </AreaChart>
                  ) : (
                    <BarChart data={dailyStackedChartData} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date_label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [fmtN(Number(value || 0)), 'เครดิต']} />
                      <Legend />
                      {stackedBankCodes.map((bankCode, idx) => (
                        <Bar
                          key={bankCode}
                          dataKey={bankCode}
                          stackId="bankCredits"
                          name={bankCode}
                          fill={["#2563eb", "#14b8a6", "#f59e0b", "#7c3aed", "#ef4444", "#06b6d4", "#84cc16"][idx % 7]}
                        />
                      ))}
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">เปรียบเทียบเครดิตที่ใช้รายธนาคาร</CardTitle>
            <CardDescription>แสดงการใช้เครดิตสะสมตามธนาคารในช่วงวันที่เลือก</CardDescription>
          </CardHeader>
          <CardContent>
            {byBankChartData.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">ไม่มีข้อมูล</div>
            ) : (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byBankChartData} margin={{ top: 10, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="bank_code" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => [fmtN(Number(value || 0)), 'เครดิต']} />
                    <Legend />
                    <Bar dataKey="credits_used" name="เครดิตที่ใช้" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">ตารางสรุปรายธนาคาร</CardTitle>
          <CardDescription>การใช้งานเครดิตและประสิทธิภาพของแต่ละธนาคาร</CardDescription>
        </CardHeader>
        <CardContent>
          {!filteredBanks.length ? (
            <div className="py-14 text-center text-sm text-gray-400">กดปุ่ม "โหลดข้อมูล" เพื่อแสดงรายงาน</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">ธนาคาร</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">เครดิตที่ใช้</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">คำขอทั้งหมด</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">สำเร็จ</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">ล้มเหลว</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">Success Rate</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b">เครดิต/คำขอ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBanks.map((row, idx) => (
                    <tr key={`${row.bank_code}-${idx}`} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="py-2 px-3 text-xs text-gray-700 font-medium">{row.bank_code}</td>
                      <td className="py-2 px-3 text-xs text-gray-700 text-right">{fmtN(row.total_credits_used)}</td>
                      <td className="py-2 px-3 text-xs text-gray-700 text-right">{fmtN(row.total_requests)}</td>
                      <td className="py-2 px-3 text-xs text-gray-700 text-right">{fmtN(row.successful_requests)}</td>
                      <td className="py-2 px-3 text-xs text-gray-700 text-right">{fmtN(row.failed_requests)}</td>
                      <td className="py-2 px-3 text-xs text-gray-700 text-right">{row.success_rate_pct.toFixed(1)}%</td>
                      <td className="py-2 px-3 text-xs text-gray-700 text-right">{row.avg_credits_per_request.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
